import {
  Camera,
  Check,
  CheckCircle,
  ClipboardCheck,
  Clock,
  FileCheck,
  Film,
  Image,
  MapPin,
  X,
  AlertCircle,
  ThumbsUp,
  ThumbsDown,
  ZoomIn,
  Download,
} from "lucide-react";
import { useProjectManagementContext } from "../context";
import ProjectSectionShell from "./ProjectSectionShell";
import EvidenceVideoPlayer from "./EvidenceVideoPlayer";
import { useMemo, useState } from "react";
import { isEvidenceVideo, resolveEvidenceMediaUrl } from "../../../lib/imageUrl";

const evidenceImageUrl = (
  ev?: { url?: string | null; image?: string | null } | string | null,
) => resolveEvidenceMediaUrl(ev);

type PreviewMedia = { url: string; kind: "photo" | "video" };

const ValidationSection = () => {
  const {
    activeValidationId,
    activeValidationItem,
    handleApproveValidation,
    setActiveValidationId,
    setIsRejectModalOpen,
    validationChecklist,
    validationItems,
  } = useProjectManagementContext() as any;

  const [selectedMedia, setSelectedMedia] = useState<PreviewMedia | null>(null);
  const [isZoomed, setIsZoomed] = useState(false);

  const evidenceList = activeValidationItem?.evidence || [];
  const { photoEvidence, videoEvidence } = useMemo(() => {
    const photos: any[] = [];
    const videos: any[] = [];
    for (const ev of evidenceList) {
      if (isEvidenceVideo(ev) || ev?.type === "video") videos.push(ev);
      else photos.push(ev);
    }
    return { photoEvidence: photos, videoEvidence: videos };
  }, [evidenceList]);

  const openPreview = (ev: any) => {
    const url = evidenceImageUrl(ev);
    if (!url) return;
    const kind: PreviewMedia["kind"] =
      isEvidenceVideo(ev) || ev?.type === "video" ? "video" : "photo";
    setSelectedMedia({ url, kind });
    setIsZoomed(true);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', label: 'Menunggu' };
      case 'review':
        return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', label: 'Ditinjau' };
      case 'approved':
        return { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Disetujui' };
      case 'rejected':
        return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200', label: 'Ditolak' };
      default:
        return { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200', label: status };
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <ProjectSectionShell
      eyebrow="Quality Gate"
      title="Validasi Pekerjaan dan Checklist SLA"
      description="Review evidence dari mobile app, cek kualitas hasil lapangan, lalu approve atau kirim revisi kembali ke aplikasi mobile tukang/serviceman."
      icon={<FileCheck size={14} />}
      tone="amber"
    >
      <div className="space-y-6">
        {/* Enhanced Queue Section */}
        <div>
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-0 mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-50 rounded-xl">
                <ClipboardCheck className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-800">Antrean Validasi</h3>
                <p className="text-sm text-slate-500">
                  {validationItems.length} pekerjaan menunggu validasi
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs px-3 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                {validationItems.filter((i: any) => i.status === 'pending').length} Pending
              </span>
              <span className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                {validationItems.filter((i: any) => i.status === 'review').length} Review
              </span>
            </div>
          </div>

          <div className="flex gap-4 pb-4 pt-1 px-1 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
            {validationItems.length === 0 ? (
              <div className="min-w-full flex items-center justify-center py-12 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h4 className="font-semibold text-slate-800">Semua Pekerjaan Sudah Tervalidasi</h4>
                  <p className="text-sm text-slate-500 mt-1">Tidak ada pekerjaan yang menunggu validasi saat ini</p>
                </div>
              </div>
            ) : (
              validationItems.map((item: any) => {
                const status = getStatusColor(item.status);
                const isActive = activeValidationId === item.id;
                const progress = item.checklists ? 
                  (item.checklists.filter((c: any) => validationChecklist[c.id]).length / item.checklists.length * 100) : 0;

                return (
                  <div
                    key={item.id}
                    className={`flex-shrink-0 w-80 cursor-pointer transition-all duration-300 ${
                      isActive ? 'scale-[1.02]' : 'hover:scale-[1.01]'
                    }`}
                    onClick={() => setActiveValidationId(item.id)}
                  >
                    <div
                      className={`rounded-2xl border-2 p-5 transition-all duration-300 ${
                        isActive
                          ? 'border-blue-500 bg-blue-50 shadow-lg shadow-blue-100'
                          : 'border-slate-200 bg-white hover:border-blue-300 hover:shadow-md'
                      }`}
                    >
                      {/* Status Badge */}
                      <div className="flex justify-between items-start mb-3">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${status.bg} ${status.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${status.text.replace('text-', 'bg-')}`} />
                          {status.label}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="w-3 h-3" />
                          {formatDate(item.completedAt || item.createdAt)}
                        </span>
                      </div>

                      {/* Title */}
                      <h6 className="font-semibold text-slate-800 text-base mb-2 line-clamp-1" title={item.name}>
                        {item.name}
                      </h6>

                      {/* Location */}
                      <div className="flex items-center gap-1.5 text-sm text-slate-500 mb-3">
                        <MapPin className="w-3.5 h-3.5" />
                        <span className="line-clamp-1">{item.location || 'Lokasi tidak tersedia'}</span>
                      </div>

                      {/* Technician */}
                      <div className="flex items-center gap-3 pt-3 border-t border-slate-200">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {item.techInitials || 'T'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 line-clamp-1">
                            {item.technician || 'Tim Lapangan'}
                          </p>
                          <div className="flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                            <span className="text-xs text-slate-400">Online</span>
                          </div>
                        </div>
                        {progress > 0 && (
                          <div className="text-right">
                            <span className="text-xs font-semibold text-blue-600">
                              {Math.round(progress)}%
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Progress Bar */}
                      {item.checklists && item.checklists.length > 0 && (
                        <div className="mt-3">
                          <div className="w-full bg-slate-200 rounded-full h-1">
                            <div 
                              className="h-1 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Main Validation Panel - Enhanced */}
        {activeValidationItem && (
          <div className="grid grid-cols-1 xl:grid-cols-[1.4fr_1fr] gap-6">
            {/* Left Panel: Visual Inspection - Enhanced */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden hover:shadow-md transition-all duration-300">
              <div className="flex items-center justify-between p-5 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-xl">
                    <Image className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">Panel Inspeksi Visual</h3>
                    <p className="text-xs text-slate-500">
                      {evidenceList.length} bukti pekerjaan
                      {videoEvidence.length > 0
                        ? ` · ${photoEvidence.length} foto · ${videoEvidence.length} video`
                        : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg">
                    <Check className="w-3 h-3 inline mr-1" />
                    {evidenceList.length} Evidence
                  </span>
                </div>
              </div>

              <div className="p-5 bg-slate-50/50 space-y-5">
                {evidenceList.length > 0 ? (
                  <>
                    {photoEvidence.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Foto
                        </p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[280px] overflow-y-auto pr-1">
                          {photoEvidence.map((ev: any, idx: number) => (
                            <div key={`photo-${idx}`} className="group relative">
                              <div className="aspect-square rounded-xl overflow-hidden bg-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer relative">
                                <img
                                  src={evidenceImageUrl(ev)}
                                  alt={`Evidence foto ${idx + 1}`}
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).src =
                                      "https://indoteknisi.net/public/assets/admin-module/img/media/service.png";
                                  }}
                                  onClick={() => openPreview(ev)}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                  <button
                                    type="button"
                                    className="p-2 bg-white rounded-full shadow-lg hover:bg-blue-50 transition-all"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPreview(ev);
                                    }}
                                  >
                                    <ZoomIn className="w-4 h-4 text-slate-700" />
                                  </button>
                                </div>
                                <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">
                                  #{idx + 1}
                                </div>
                                <div className="absolute bottom-2 right-2 bg-black/40 text-white text-xs px-2 py-0.5 rounded-full backdrop-blur-sm">
                                  <Camera className="w-3 h-3 inline mr-1" />
                                  Foto
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {videoEvidence.length > 0 && (
                      <div>
                        <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                          <Film className="h-3.5 w-3.5" />
                          Video
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[420px] overflow-y-auto pr-1">
                          {videoEvidence.map((ev: any, idx: number) => {
                            const url = evidenceImageUrl(ev);
                            return (
                              <div key={`video-${idx}`} className="space-y-2">
                                <EvidenceVideoPlayer url={url} />
                                <div className="flex items-center justify-between gap-2 px-0.5">
                                  <span className="text-xs text-slate-500">
                                    Video #{idx + 1}
                                    {ev.duration_seconds
                                      ? ` · ${ev.duration_seconds}s`
                                      : ""}
                                  </span>
                                  <button
                                    type="button"
                                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                                    onClick={() => openPreview(ev)}
                                  >
                                    Perbesar
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                      <Camera className="w-10 h-10 text-slate-300" />
                    </div>
                    <h4 className="font-semibold text-slate-600">Belum Ada Bukti Pengerjaan</h4>
                    <p className="text-sm text-slate-400 mt-1">Belum ada evidence yang tersinkron dari mobile app</p>
                  </div>
                )}
              </div>

              {/* Task Notes from Technician */}
              {activeValidationItem.notes && (
                <div className="px-5 pb-5 bg-slate-50/50">
                  <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm">
                    <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Catatan Laporan / Deskripsi</h4>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{activeValidationItem.notes}</p>
                  </div>
                </div>
              )}

              {/* Task Summary */}
              <div className="p-4 border-t border-slate-200 bg-slate-50/50">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Task ID</p>
                    <p className="font-mono text-sm font-semibold text-slate-700">#{activeValidationItem.id?.slice(0, 8) || 'N/A'}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Selesai</p>
                    <p className="text-sm font-semibold text-emerald-600">
                      {formatDate(activeValidationItem.completedAt)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-slate-500">Status</p>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${getStatusColor(activeValidationItem.status).text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${getStatusColor(activeValidationItem.status).text.replace('text-', 'bg-')}`} />
                      {getStatusColor(activeValidationItem.status).label}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel: Validation Actions - Simplified */}
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full">
              <div className="p-5 border-b border-slate-200 bg-gradient-to-r from-purple-50 to-white rounded-t-2xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-50 rounded-xl">
                    <FileCheck className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">Validasi & Persetujuan</h3>
                    <p className="text-xs text-slate-500">
                      Evaluasi hasil pekerjaan
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 p-5 overflow-y-auto max-h-[420px]">
                <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200 mb-5">
                  <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-700">
                    <p className="font-medium">Instruksi Validasi</p>
                    <p className="mt-0.5">Lakukan evaluasi terhadap bukti pekerjaan di panel kiri. Jika hasil sesuai dan memuaskan, klik <strong>Approve & Finalize</strong>. Jika hasil kurang memuaskan, klik <strong className="text-red-600">Reject ke Mobile</strong> agar revisi dikirim kembali ke aplikasi mobile tukang/serviceman.</p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-5 border-t border-slate-200 bg-slate-50/50 rounded-b-2xl">
                <div className="flex flex-col sm:grid sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setIsRejectModalOpen(true)}
                    className="flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 border-2 border-red-200 text-red-600 font-semibold rounded-xl transition-all duration-200 hover:scale-[1.02]"
                  >
                    <ThumbsDown className="w-4 h-4" />
                    Reject ke Mobile
                  </button>
                  <button
                    onClick={() =>
                      activeValidationId !== null &&
                      handleApproveValidation(
                        activeValidationId,
                        activeValidationItem
                      )
                    }
                    className="flex items-center justify-center gap-2 px-4 py-3 font-semibold rounded-xl transition-all duration-200 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white hover:scale-[1.02] shadow-lg shadow-emerald-200"
                  >
                    <ThumbsUp className="w-4 h-4" />
                    Approve & Finalize
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Evidence Zoom / Play Modal */}
        {isZoomed && selectedMedia && (
          <div 
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 animate-fadeIn"
            onClick={() => setIsZoomed(false)}
          >
            <div 
              className="relative max-w-4xl w-full max-h-[95vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-4 border-b border-slate-200 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg">
                    {selectedMedia.kind === "video" ? (
                      <Film className="w-4 h-4 text-blue-600" />
                    ) : (
                      <Image className="w-4 h-4 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-800">
                      {selectedMedia.kind === "video" ? "Putar Video Evidence" : "Preview Evidence"}
                    </h4>
                    <p className="text-xs text-slate-500">Klik di luar untuk menutup</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsZoomed(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="p-3 sm:p-4 bg-slate-100 overflow-auto">
                {selectedMedia.kind === "video" ? (
                  <EvidenceVideoPlayer
                    url={selectedMedia.url}
                    className="mx-auto"
                  />
                ) : (
                  <img
                    src={selectedMedia.url}
                    alt="Evidence Zoom"
                    className="w-full h-auto max-h-[70vh] object-contain rounded-lg mx-auto"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src =
                        "https://indoteknisi.net/public/assets/admin-module/img/media/service.png";
                    }}
                  />
                )}
              </div>
              <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-between items-center shrink-0 gap-3">
                <span className="text-sm text-slate-500">
                  {selectedMedia.kind === "video" ? (
                    <Film className="w-4 h-4 inline mr-2" />
                  ) : (
                    <Camera className="w-4 h-4 inline mr-2" />
                  )}
                  {selectedMedia.kind === "video" ? "Video" : "Foto"} evidence
                </span>
                <button
                  type="button"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all text-sm font-medium flex items-center gap-2"
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = selectedMedia.url;
                    link.download =
                      selectedMedia.kind === "video"
                        ? `evidence-${Date.now()}.mp4`
                        : `evidence-${Date.now()}.jpg`;
                    link.target = "_blank";
                    link.rel = "noopener noreferrer";
                    link.click();
                  }}
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add some custom styles for scrollbar */}
      <style>{`
        .scrollbar-thin::-webkit-scrollbar {
          height: 6px;
        }
        .scrollbar-thin::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 8px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 8px;
        }
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </ProjectSectionShell>
  );
};

export default ValidationSection;
